import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import BackToTopButton from '../../components/feature/BackToTopButton';
import Footer from '../home/components/Footer';
import WaitlistFAQ from './components/WaitlistFAQ';

// ── Referral helpers ──────────────────────────────────────────────────────────
function generateReferralCode(firstName: string, email: string): string {
  const base = (firstName.slice(0, 3) + email.slice(0, 4))
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, 'X');
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `${base}${suffix}`;
}

const SHARE_MILESTONES = [
  { count: 1, reward: 'Skip 50 spots', icon: 'ri-arrow-up-double-line' },
  { count: 3, reward: 'Skip 200 spots + Beta badge', icon: 'ri-medal-line' },
  { count: 5, reward: 'Founding Member status', icon: 'ri-vip-crown-line' },
  { count: 10, reward: 'Lifetime 30% discount', icon: 'ri-percent-line' },
];

// ── Referral Panel ────────────────────────────────────────────────────────────
function ReferralPanel({ firstName, email }: { firstName: string; email: string }) {
  const [copied, setCopied] = useState(false);
  const [referralCount] = useState(0);
  const referralCode = useRef(generateReferralCode(firstName, email)).current;
  const referralUrl = `${window.location.origin}/waitlist?ref=${referralCode}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const shareLinks = [
    {
      label: 'Twitter / X',
      icon: 'ri-twitter-x-line',
      color: 'hover:bg-white/10',
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        `I just joined the WingmanFix waitlist — the AI copilot for pilots! 🛩️ Join me and skip the line: ${referralUrl}`
      )}`,
    },
    {
      label: 'Facebook',
      icon: 'ri-facebook-line',
      color: 'hover:bg-[#1877F2]/20',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}`,
    },
    {
      label: 'WhatsApp',
      icon: 'ri-whatsapp-line',
      color: 'hover:bg-[#25D366]/20',
      href: `https://wa.me/?text=${encodeURIComponent(
        `Join me on WingmanFix — the AI copilot for pilots! 🛩️ Use my link to skip the waitlist: ${referralUrl}`
      )}`,
    },
    {
      label: 'Email',
      icon: 'ri-mail-send-line',
      color: 'hover:bg-[#00FFD1]/10',
      href: `mailto:?subject=${encodeURIComponent('Join me on WingmanFix!')}&body=${encodeURIComponent(
        `Hey!\n\nI just joined the WingmanFix waitlist — it's an AI copilot app for pilots that analyzes your flights and helps you improve faster.\n\nUse my referral link to join and we both move up the list:\n${referralUrl}\n\nBlue skies!`
      )}`,
    },
  ];

  const nextMilestone = SHARE_MILESTONES.find((m) => m.count > referralCount) ?? SHARE_MILESTONES[SHARE_MILESTONES.length - 1];
  const progressPct = Math.min((referralCount / nextMilestone.count) * 100, 100);

  return (
    <div className="space-y-6">
      {/* ── Confirmation banner ── */}
      <div className="bg-white/5 border border-[#00FFD1]/30 rounded-2xl p-8 text-center">
        <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5 bg-[#00FFD1]/10 rounded-full border border-[#00FFD1]/30">
          <i className="ri-check-double-line text-[#00FFD1] text-4xl" />
        </div>
        <h2 className="text-3xl font-bold mb-2">You're cleared for departure!</h2>
        <p className="text-gray-400 leading-relaxed max-w-sm mx-auto mb-4">
          You're on the list. Invite fellow pilots to move up and unlock exclusive rewards.
        </p>
        <div className="inline-block bg-[#00FFD1]/10 border border-[#00FFD1]/20 rounded-full px-5 py-2 text-[#00FFD1] text-xs font-semibold uppercase tracking-widest">
          CLEARED FOR DEPARTURE ✓
        </div>
      </div>

      {/* ── Referral link card ── */}
      <div className="bg-white/5 border border-[#00FFD1]/20 rounded-2xl p-7">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-links-line text-[#00FFD1] text-lg" />
          </div>
          <h3 className="text-sm font-bold text-[#00FFD1] uppercase tracking-widest">Your Referral Link</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Share this link — every pilot who joins through it moves you up the waitlist.
        </p>

        {/* Link copy row */}
        <div className="flex items-center gap-2 bg-[#0A1628] border border-white/20 rounded-xl px-4 py-3 mb-5">
          <span className="flex-1 text-xs text-gray-300 truncate font-mono">{referralUrl}</span>
          <button
            onClick={copyLink}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              copied
                ? 'bg-[#00FFD1]/20 text-[#00FFD1] border border-[#00FFD1]/40'
                : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
            </div>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        {/* Share buttons */}
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Share via</p>
        <div className="grid grid-cols-4 gap-3">
          {shareLinks.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex flex-col items-center gap-2 border border-white/10 rounded-xl py-3 px-2 transition-all cursor-pointer ${s.color}`}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${s.icon} text-white text-lg`} />
              </div>
              <span className="text-xs text-gray-400">{s.label}</span>
            </a>
          ))}
        </div>
      </div>

      {/* ── Progress tracker ── */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-7">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-bar-chart-grouped-line text-[#00FFD1] text-lg" />
            </div>
            <h3 className="text-sm font-bold text-[#00FFD1] uppercase tracking-widest">Your Progress</h3>
          </div>
          <div className="bg-[#00FFD1]/10 border border-[#00FFD1]/20 rounded-full px-3 py-1 text-[#00FFD1] text-xs font-bold">
            {referralCount} / {nextMilestone.count} referrals
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-gradient-to-r from-[#00FFD1] to-[#00A3E0] rounded-full transition-all duration-700"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Milestones */}
        <div className="space-y-3">
          {SHARE_MILESTONES.map((m) => {
            const unlocked = referralCount >= m.count;
            return (
              <div
                key={m.count}
                className={`flex items-center gap-4 rounded-xl px-4 py-3 border transition-all ${
                  unlocked
                    ? 'border-[#00FFD1]/40 bg-[#00FFD1]/5'
                    : 'border-white/10 bg-white/3 opacity-60'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-full border ${unlocked ? 'border-[#00FFD1]/50 bg-[#00FFD1]/10' : 'border-white/20 bg-white/5'}`}>
                  <i className={`${m.icon} text-base ${unlocked ? 'text-[#00FFD1]' : 'text-gray-500'}`} />
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${unlocked ? 'text-white' : 'text-gray-500'}`}>
                    {m.reward}
                  </div>
                  <div className="text-xs text-gray-600">
                    Invite {m.count} pilot{m.count > 1 ? 's' : ''}
                  </div>
                </div>
                {unlocked ? (
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-circle-fill text-[#00FFD1] text-lg" />
                  </div>
                ) : (
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-lock-line text-gray-600 text-base" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Referral code badge ── */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-semibold">Your Referral Code</p>
          <p className="text-xl font-bold font-mono text-white tracking-widest">{referralCode}</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/"
            className="border border-white/20 text-gray-300 hover:text-white hover:border-white/40 font-semibold px-4 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
          >
            Back to Home
          </Link>
          <Link
            to="/features"
            className="border border-[#00FFD1]/40 text-[#00FFD1] hover:bg-[#00FFD1]/10 font-semibold px-4 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
          >
            Explore Features
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function WaitlistPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    aircraftType: '',
    flyingHours: '',
    homeAirport: '',
    trainingGoals: '',
    heardFrom: '',
    newsletter: false,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      if (name === 'trainingGoals') setCharCount(value.length);
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) {
      setError('Training goals must be 500 characters or fewer.');
      return;
    }
    setError('');
    setLoading(true);

    const body = new URLSearchParams();
    Object.entries(form).forEach(([key, val]) => body.append(key, String(val)));

    try {
      await fetch('https://readdy.ai/api/form/d6sa09s4k19g20dvs29g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navLinks = [
    { label: 'Features', to: '/features' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Safety', to: '/safety' },
  ];

  return (
    <div className="min-h-screen bg-[#060E1E] text-white font-sans">
      {/* Nav */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        } border-b border-[#00FFD1]/20`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/2de32bf8-6894-4f4a-ba90-b9480aaa88cf.png"
              alt="WingmanFix Logo"
              className="h-10 w-10 object-contain"
            />
            <span className="text-xl font-bold tracking-tight">WingmanFix</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-sm text-gray-300 hover:text-[#00FFD1] transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <button
            className="md:hidden flex flex-col justify-center items-center w-9 h-9 gap-1.5 cursor-pointer"
            onClick={() => setMenuOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            <span className={`block h-0.5 w-6 bg-[#00FFD1] transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block h-0.5 w-6 bg-[#00FFD1] transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-0.5 w-6 bg-[#00FFD1] transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>

        <div className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'} bg-[#0A1628]/98 border-t border-[#00FFD1]/10`}>
          <div className="flex flex-col px-6 py-4 gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className="text-sm text-gray-300 hover:text-[#00FFD1] transition-colors uppercase tracking-wider cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#060E1E] to-[#060E1E]">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                'linear-gradient(#00FFD1 1px, transparent 1px), linear-gradient(90deg, #00FFD1 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="absolute top-24 left-6 w-10 h-10 border-l-2 border-t-2 border-[#00FFD1]/30" />
        <div className="absolute top-24 right-6 w-10 h-10 border-r-2 border-t-2 border-[#00FFD1]/30" />

        <div className="max-w-3xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-5 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse" />
            <span className="text-[#00FFD1] text-xs font-semibold uppercase tracking-widest">
              {submitted ? 'Welcome Aboard' : 'Limited Early Access'}
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold leading-tight mb-4">
            {submitted ? (
              <>Spread Your <span className="text-[#00FFD1]">Wings</span></>
            ) : (
              <>Join the <span className="text-[#00FFD1]">WingmanFix</span> Waitlist</>
            )}
          </h1>
          <p className="text-gray-400 text-lg leading-relaxed max-w-xl mx-auto">
            {submitted
              ? 'Invite fellow pilots using your personal link and climb the waitlist — the more you share, the sooner you fly.'
              : 'Tell us about yourself and your flying. We use this to match you with the right features and prioritize your access.'}
          </p>

          {!submitted && (
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg mx-auto">
              {[
                { value: '340+', label: 'Pilots waiting' },
                { value: 'Free', label: 'During beta' },
                { value: '< 2 min', label: 'To sign up' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl py-4 px-3">
                  <div className="text-2xl font-bold text-[#00FFD1]">{stat.value}</div>
                  <div className="text-xs text-gray-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-3">Simple Process</p>
            <h2 className="text-3xl font-bold">How It Works</h2>
            <p className="text-gray-400 mt-3 text-sm max-w-md mx-auto">
              Three easy steps to secure your spot and get early access to WingmanFix.
            </p>
          </div>

          <div className="relative grid md:grid-cols-3 gap-6">
            {/* Connector line (desktop) */}
            <div className="hidden md:block absolute top-12 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-px bg-gradient-to-r from-[#00FFD1]/40 via-[#00FFD1]/20 to-[#00FFD1]/40" />

            {/* Step 1 */}
            <div className="relative bg-white/5 border border-[#00FFD1]/20 rounded-2xl p-7 flex flex-col items-center text-center hover:border-[#00FFD1]/50 hover:bg-white/8 transition-all duration-300 group">
              <div className="relative mb-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#00FFD1]/10 border-2 border-[#00FFD1]/40 group-hover:bg-[#00FFD1]/20 transition-all">
                  <i className="ri-user-add-line text-[#00FFD1] text-2xl" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-[#00FFD1] text-[#060E1E] text-xs font-black">
                  1
                </div>
              </div>
              <h3 className="text-base font-bold mb-2">Sign Up</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Fill out the short form below with your pilot profile. Takes less than 2 minutes — no credit card needed.
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-[#00FFD1]/70 font-semibold uppercase tracking-wider">
                <i className="ri-time-line" /> &lt; 2 minutes
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative bg-white/5 border border-white/10 rounded-2xl p-7 flex flex-col items-center text-center hover:border-[#00FFD1]/50 hover:bg-white/8 transition-all duration-300 group">
              <div className="relative mb-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#00FFD1]/10 border-2 border-[#00FFD1]/40 group-hover:bg-[#00FFD1]/20 transition-all">
                  <i className="ri-share-forward-line text-[#00FFD1] text-2xl" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-[#00FFD1] text-[#060E1E] text-xs font-black">
                  2
                </div>
              </div>
              <h3 className="text-base font-bold mb-2">Share Your Link</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Get a personal referral link. Share it with fellow pilots — every sign-up through your link moves you up the list.
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-[#00FFD1]/70 font-semibold uppercase tracking-wider">
                <i className="ri-arrow-up-double-line" /> Skip the queue
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative bg-white/5 border border-white/10 rounded-2xl p-7 flex flex-col items-center text-center hover:border-[#00FFD1]/50 hover:bg-white/8 transition-all duration-300 group">
              <div className="relative mb-5">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#00FFD1]/10 border-2 border-[#00FFD1]/40 group-hover:bg-[#00FFD1]/20 transition-all">
                  <i className="ri-rocket-2-line text-[#00FFD1] text-2xl" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-[#00FFD1] text-[#060E1E] text-xs font-black">
                  3
                </div>
              </div>
              <h3 className="text-base font-bold mb-2">Get Early Access</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                When your spot opens up, we'll email you an exclusive invite. Beta access is completely free.
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-[#00FFD1]/70 font-semibold uppercase tracking-wider">
                <i className="ri-gift-line" /> Free during beta
              </div>
            </div>
          </div>

          {/* Bottom hint */}
          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-500">
            <i className="ri-arrow-down-line text-[#00FFD1]/60 animate-bounce" />
            <span>Start with Step 1 — fill in the form below</span>
            <i className="ri-arrow-down-line text-[#00FFD1]/60 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Form / Referral Section */}
      <section className="pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          {submitted ? (
            <ReferralPanel firstName={form.firstName} email={form.email} />
          ) : (
            <form
              data-readdy-form
              id="waitlist-detailed-form"
              onSubmit={handleSubmit}
              className="bg-white/5 border border-[#00FFD1]/20 rounded-2xl p-8 sm:p-10 space-y-8"
            >
              {/* Section: Personal Info */}
              <div>
                <h2 className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-5 flex items-center gap-2">
                  <i className="ri-user-line" /> Personal Info
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      First Name <span className="text-[#00FFD1]">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="Alex"
                      className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00FFD1]/60 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Last Name <span className="text-[#00FFD1]">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="Reynolds"
                      className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00FFD1]/60 transition-all"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Email Address <span className="text-[#00FFD1]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="captain@example.com"
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00FFD1]/60 transition-all"
                  />
                </div>
              </div>

              <div className="border-t border-white/10" />

              {/* Section: Flying Background */}
              <div>
                <h2 className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-5 flex items-center gap-2">
                  <i className="ri-flight-takeoff-line" /> Flying Background
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Pilot Role <span className="text-[#00FFD1]">*</span>
                    </label>
                    <select
                      name="role"
                      required
                      value={form.role}
                      onChange={handleChange}
                      className="w-full bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00FFD1]/60 transition-all cursor-pointer appearance-none"
                    >
                      <option value="" disabled>Select your role…</option>
                      <option value="Student Pilot">Student Pilot</option>
                      <option value="Private Pilot (PPL)">Private Pilot (PPL)</option>
                      <option value="Instrument Rated (IFR)">Instrument Rated (IFR)</option>
                      <option value="Commercial Pilot (CPL)">Commercial Pilot (CPL)</option>
                      <option value="CFI / Flight Instructor">CFI / Flight Instructor</option>
                      <option value="ATP / Airline Pilot">ATP / Airline Pilot</option>
                      <option value="Aviation Enthusiast">Aviation Enthusiast</option>
                    </select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Primary Aircraft Type <span className="text-[#00FFD1]">*</span>
                      </label>
                      <select
                        name="aircraftType"
                        required
                        value={form.aircraftType}
                        onChange={handleChange}
                        className="w-full bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00FFD1]/60 transition-all cursor-pointer appearance-none"
                      >
                        <option value="" disabled>Select aircraft…</option>
                        <option value="Single-Engine Piston (SEP)">Single-Engine Piston (SEP)</option>
                        <option value="Multi-Engine Piston (MEP)">Multi-Engine Piston (MEP)</option>
                        <option value="Turboprop">Turboprop</option>
                        <option value="Light Sport Aircraft (LSA)">Light Sport Aircraft (LSA)</option>
                        <option value="Helicopter">Helicopter</option>
                        <option value="Glider / Sailplane">Glider / Sailplane</option>
                        <option value="Jet / Business Aviation">Jet / Business Aviation</option>
                        <option value="Simulator Only">Simulator Only</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Total Flying Hours <span className="text-[#00FFD1]">*</span>
                      </label>
                      <select
                        name="flyingHours"
                        required
                        value={form.flyingHours}
                        onChange={handleChange}
                        className="w-full bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00FFD1]/60 transition-all cursor-pointer appearance-none"
                      >
                        <option value="" disabled>Select range…</option>
                        <option value="0–50 hours">0–50 hours (Student)</option>
                        <option value="50–200 hours">50–200 hours</option>
                        <option value="200–500 hours">200–500 hours</option>
                        <option value="500–1,000 hours">500–1,000 hours</option>
                        <option value="1,000–5,000 hours">1,000–5,000 hours</option>
                        <option value="5,000+ hours">5,000+ hours</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Home Airport (ICAO / IATA)
                    </label>
                    <input
                      type="text"
                      name="homeAirport"
                      value={form.homeAirport}
                      onChange={handleChange}
                      placeholder="e.g. KLAX, EGLL, YSSY"
                      className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00FFD1]/60 transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10" />

              {/* Section: Goals */}
              <div>
                <h2 className="text-xs font-bold text-[#00FFD1] uppercase tracking-widest mb-5 flex items-center gap-2">
                  <i className="ri-target-line" /> Your Goals
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      What are your main training goals?
                    </label>
                    <textarea
                      name="trainingGoals"
                      value={form.trainingGoals}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      placeholder="e.g. Improve IFR proficiency, work on crosswind landings, prepare for instrument checkride…"
                      className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#00FFD1]/60 transition-all resize-none"
                    />
                    <div className={`text-right text-xs mt-1 ${charCount > 480 ? 'text-amber-400' : 'text-gray-600'}`}>
                      {charCount}/500
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      How did you hear about WingmanFix?
                    </label>
                    <select
                      name="heardFrom"
                      value={form.heardFrom}
                      onChange={handleChange}
                      className="w-full bg-[#0A1628] border border-white/20 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[#00FFD1]/60 transition-all cursor-pointer appearance-none"
                    >
                      <option value="" disabled>Select source…</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Friend / Colleague">Friend / Colleague</option>
                      <option value="Flight School">Flight School</option>
                      <option value="Aviation Forum / Reddit">Aviation Forum / Reddit</option>
                      <option value="Podcast">Podcast</option>
                      <option value="Google Search">Google Search</option>
                      <option value="CFI Recommendation">CFI Recommendation</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10" />

              {/* Newsletter checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    name="newsletter"
                    checked={form.newsletter}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${form.newsletter ? 'bg-[#00FFD1] border-[#00FFD1]' : 'border-white/30 bg-white/5 group-hover:border-[#00FFD1]/50'}`}>
                    {form.newsletter && <i className="ri-check-line text-[#060E1E] text-xs font-bold" />}
                  </div>
                </div>
                <span className="text-sm text-gray-400 leading-relaxed">
                  Send me product updates, aviation tips, and early access news. No spam — unsubscribe anytime.
                </span>
              </label>

              {error && (
                <p className="text-red-400 text-xs flex items-center gap-1">
                  <i className="ri-error-warning-line" /> {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || charCount > 500}
                className="w-full bg-[#00A3E0] hover:bg-[#0088BB] disabled:opacity-60 text-white font-bold py-4 rounded-lg transition-all hover:scale-[1.02] shadow-lg hover:shadow-[#00A3E0]/30 cursor-pointer whitespace-nowrap text-sm"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Submitting…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-line" /> Request Early Access
                  </span>
                )}
              </button>

              <p className="text-center text-xs text-gray-600">
                No credit card required. Your data stays private and secure.{' '}
                <Link to="/privacy" className="text-[#00FFD1]/60 hover:text-[#00FFD1] underline transition-colors cursor-pointer">
                  Privacy Policy
                </Link>
              </p>
            </form>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <WaitlistFAQ />

      <Footer />
      <BackToTopButton />
    </div>
  );
}
